import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { registerAuthContext } from './index';
import { getCognitoLogoutUrl } from '@/config/cognito';
import { hasCognitoGroup as checkCognitoGroup } from '@/utils/cognitoGroups';

const AuthContext = createContext();
registerAuthContext(AuthContext);

/**
 * AuthProvider simplificado que integra com AWS Cognito via react-oidc-context
 * Usa apenas dados do Cognito, sem buscar perfil no backend
 */
export const AuthProvider = ({ children }) => {
  const oidcAuth = useOidcAuth();
  const [user, setUser] = useState(null);

  // Sincroniza dados do Cognito com o estado do contexto
  useEffect(() => {
    if (oidcAuth.isLoading) {
      return;
    }

    if (oidcAuth.isAuthenticated && oidcAuth.user) {
      const cognitoUser = {
        ...oidcAuth.user,
        // Mantém compatibilidade com código existente
        name: oidcAuth.user.profile?.name || oidcAuth.user.profile?.email || 'Usuário',
        email: oidcAuth.user.profile?.email || '',
        token: oidcAuth.user.id_token,
        accessToken: oidcAuth.user.access_token,
        idToken: oidcAuth.user.id_token,
        refreshToken: oidcAuth.user.refresh_token,
      };

      setUser(cognitoUser);
    } else {
      setUser(null);
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.user, oidcAuth.isLoading]);

  // Helper para extrair informações do usuário do Cognito
  const userInfo = useMemo(() => {
    if (!user) return null;
    return {
      name: user.profile?.name || user.profile?.email || 'Usuário',
      email: user.profile?.email || '',
      phone: user.profile?.phone_number || '',
      sub: user.profile?.sub || '', // Cognito user ID
    };
  }, [user]);

  // Logout que integra com Cognito
  const logout = useCallback(async () => {
    console.log('Fazendo logout...');

    try {
      await oidcAuth.removeUser();
    } catch (error) {
      console.warn('Falha ao limpar usuario local:', error);
    }

    setUser(null);

    const hasHostedDomain = Boolean(process.env.REACT_APP_COGNITO_HOSTED_DOMAIN);
    const isCognitoHostedUi = hasHostedDomain || oidcAuth?.settings?.authority?.includes('amazoncognito.com');

    if (isCognitoHostedUi) {
      try {
        const logoutUrl = getCognitoLogoutUrl();
        window.location.assign(logoutUrl);
        return;
      } catch (error) {
        console.warn('Falha ao montar logout do Cognito:', error);
        window.location.assign('/log_in');
        return;
      }
    }

    if (oidcAuth.signoutRedirect) {
      try {
        await oidcAuth.signoutRedirect();
        return;
      } catch (error) {
        console.warn('Falha ao iniciar logout OIDC:', error);
      }
    }

    window.location.assign('/log_in');
  }, [oidcAuth]);

  const hasCognitoGroup = useCallback((groupName) => {
    return checkCognitoGroup(user, groupName);
  }, [user]);

  const value = useMemo(() => ({
    user, // Raw Cognito user object
    userInfo, // Parsed user info
    isAuthenticated: oidcAuth.isAuthenticated,
    isLoading: oidcAuth.isLoading,
    logout,
    login: oidcAuth.signinRedirect,
    hasCognitoGroup,
  }), [
    user,
    userInfo,
    oidcAuth.isAuthenticated,
    oidcAuth.isLoading,
    oidcAuth.signinRedirect,
    logout,
    hasCognitoGroup,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
